import Phaser from "phaser";
import type { PlayerFull } from "../../api/me";
import type { TelegramContext } from "../../telegram";

interface Line {
  text: string;
  size: string;
  color?: string;
  alpha?: number;
  spacingAfter?: number; // dodatkowy odstep po linii (px)
}

export class BootScene extends Phaser.Scene {
  private centerTexts: Phaser.GameObjects.Text[] = [];
  private lineMeta: Line[] = [];
  private tapHint!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: "BootScene" });
  }

  create(): void {
    const telegram = this.registry.get("telegram") as TelegramContext;
    const playerFull = this.registry.get("playerFull") as PlayerFull;
    const dpr = (this.registry.get("dpr") as number | undefined) ?? 1;
    const textColor = telegram.themeParams.text_color ?? "#ffffff";
    const { player, location } = playerFull;

    // Paleta BG (z docs/ASSETS.md § 2)
    const COLOR_GOLD = "#C9A961";
    const COLOR_HP = "#8B2E2E";
    const COLOR_MANA = "#4477FF";
    const COLOR_STAMINA = "#4A5D3A";
    const COLOR_DIM = textColor;

    this.lineMeta = [
      // Header — lokacja
      { text: location.namePl, size: "28px", color: COLOR_GOLD, spacingAfter: 24 },
      // Imie + level
      { text: player.displayName, size: "40px" },
      { text: `Poziom ${player.level}`, size: "20px", alpha: 0.7, spacingAfter: 20 },
      // Staty
      { text: `HP: ${player.currentHp}/${player.baseStats.hp}`, size: "16px", color: COLOR_HP },
      { text: `Mana: ${player.currentMana}/${player.baseStats.mana}`, size: "16px", color: COLOR_MANA },
      {
        text: `Stamina: ${player.currentStamina}/${player.baseStats.stamina_max}`,
        size: "16px",
        color: COLOR_STAMINA,
        spacingAfter: 16,
      },
      // Zloto
      { text: `Zloto: ${player.gold}`, size: "16px", color: COLOR_GOLD, spacingAfter: 16 },
      // Debug pozycja
      { text: `Pozycja: ${player.currentPositionX}, ${player.currentPositionY}`, size: "11px", alpha: 0.4 },
      { text: `EXP: ${player.experience} | Equipped: ${playerFull.equipped.length} | Bag: ${playerFull.inventory.length}`, size: "11px", alpha: 0.4 },
    ];

    this.centerTexts = this.lineMeta.map((line) =>
      this.add
        .text(0, 0, line.text, {
          fontFamily: "sans-serif",
          fontSize: line.size,
          color: line.color ?? COLOR_DIM,
        })
        .setOrigin(0.5)
        .setAlpha(line.alpha ?? 1)
        .setResolution(dpr),
    );

    // Tap hint na dole — gracz klika gdziekolwiek żeby przejść do WorldScene
    this.tapHint = this.add
      .text(0, 0, "▸ Tap aby wejsc do gry ◂", {
        fontFamily: "sans-serif",
        fontSize: "18px",
        color: COLOR_GOLD,
      })
      .setOrigin(0.5)
      .setAlpha(0.9)
      .setResolution(dpr);

    this.tweens.add({
      targets: this.tapHint,
      alpha: 0.4,
      duration: 800,
      yoyo: true,
      repeat: -1,
    });

    this.layoutCenter();

    this.input.once("pointerdown", () => {
      this.scene.start("WorldScene");
    });

    this.scale.on("resize", (gameSize: Phaser.Structs.Size) => {
      this.cameras.resize(gameSize.width, gameSize.height);
      this.layoutCenter();
    });
  }

  private layoutCenter(): void {
    const w = this.scale.width;
    const h = this.scale.height;

    // Policz pelny rozmiar bloku zeby vertically center
    let totalHeight = 0;
    this.centerTexts.forEach((t, i) => {
      totalHeight += t.height + (this.lineMeta[i]?.spacingAfter ?? 4);
    });

    let y = h / 2 - totalHeight / 2;
    this.centerTexts.forEach((t, i) => {
      t.setPosition(w / 2, y + t.height / 2);
      y += t.height + (this.lineMeta[i]?.spacingAfter ?? 4);
    });

    // Tap hint na dole
    if (this.tapHint) {
      this.tapHint.setPosition(w / 2, h - 40);
    }
  }
}
